// Helper functions for attendance management

/**
 * Calculate attendance rate for a student or class
 * @param {Array} attendanceRecords - Array of attendance records
 * @param {string} studentId - Optional student ID to filter by
 * @param {string} className - Optional class name to filter by
 * @param {string} startDate - Start date for calculation period
 * @param {string} endDate - End date for calculation period
 * @returns {number} - Attendance rate as a percentage (0-100)
 */
export const calculateAttendanceRate = (attendanceRecords, studentId = null, className = null, startDate = null, endDate = null) => {
  try {
    if (!Array.isArray(attendanceRecords) || attendanceRecords.length === 0) {
      return 0;
    }

    // Filter records based on parameters
    let filteredRecords = attendanceRecords;

    if (studentId) {
      filteredRecords = filteredRecords.filter(record => record.studentId === studentId);
    }

    if (className) {
      filteredRecords = filteredRecords.filter(record => record.class === className);
    }

    if (startDate && endDate) {
      filteredRecords = filteredRecords.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= new Date(startDate) && recordDate <= new Date(endDate);
      });
    }

    if (filteredRecords.length === 0) {
      return 0;
    }

    // Count present and late as attended
    const attendedCount = filteredRecords.filter(record => 
      record.status === 'present' || record.status === 'late'
    ).length;

    const totalCount = filteredRecords.length;
    const attendanceRate = (attendedCount / totalCount) * 100;

    return Math.round(attendanceRate * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error('Error calculating attendance rate:', error);
    return 0;
  }
};

/**
 * Get attendance status based on attendance rate
 * @param {number} attendanceRate - Attendance rate percentage
 * @returns {string} - Status description
 */
export const getAttendanceStatus = (attendanceRate) => {
  if (attendanceRate >= 95) {
    return '優秀';
  } else if (attendanceRate >= 90) {
    return '良好';
  } else if (attendanceRate >= 80) {
    return '一般';
  } else if (attendanceRate >= 70) {
    return '需要改善';
  } else {
    return '嚴重缺勤';
  }
};

/**
 * Get attendance statistics for a class
 * @param {Array} attendanceRecords - Array of attendance records
 * @param {string} className - Class name
 * @param {string} date - Specific date to analyze
 * @returns {Object} - Attendance statistics
 */
export const getClassAttendanceStats = (attendanceRecords, className, date = null) => {
  try {
    let filteredRecords = attendanceRecords.filter(record => record.class === className);
    
    if (date) {
      filteredRecords = filteredRecords.filter(record => record.date === date);
    }

    if (filteredRecords.length === 0) {
      return {
        total: 0,
        present: 0,
        late: 0,
        absent: 0,
        presentRate: 0,
        lateRate: 0,
        absentRate: 0
      };
    }

    const total = filteredRecords.length;
    const present = filteredRecords.filter(record => record.status === 'present').length;
    const late = filteredRecords.filter(record => record.status === 'late').length;
    const absent = filteredRecords.filter(record => record.status === 'absent').length;

    return {
      total,
      present,
      late,
      absent,
      presentRate: Math.round((present / total) * 100 * 100) / 100,
      lateRate: Math.round((late / total) * 100 * 100) / 100,
      absentRate: Math.round((absent / total) * 100 * 100) / 100
    };
  } catch (error) {
    console.error('Error getting class attendance stats:', error);
    return {
      total: 0,
      present: 0,
      late: 0,
      absent: 0,
      presentRate: 0,
      lateRate: 0,
      absentRate: 0
    };
  }
};

/**
 * Get student attendance summary
 * @param {Array} attendanceRecords - Array of attendance records
 * @param {string} studentId - Student ID
 * @param {string} startDate - Start date for summary period
 * @param {string} endDate - End date for summary period
 * @returns {Object} - Student attendance summary
 */
export const getStudentAttendanceSummary = (attendanceRecords, studentId, startDate = null, endDate = null) => {
  try {
    let filteredRecords = attendanceRecords.filter(record => record.studentId === studentId);
    
    if (startDate && endDate) {
      filteredRecords = filteredRecords.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate >= new Date(startDate) && recordDate <= new Date(endDate);
      });
    }

    if (filteredRecords.length === 0) {
      return {
        totalDays: 0,
        presentDays: 0,
        lateDays: 0,
        absentDays: 0,
        attendanceRate: 0,
        status: '無記錄'
      };
    }

    const totalDays = filteredRecords.length;
    const presentDays = filteredRecords.filter(record => record.status === 'present').length;
    const lateDays = filteredRecords.filter(record => record.status === 'late').length;
    const absentDays = filteredRecords.filter(record => record.status === 'absent').length;
    const attendanceRate = calculateAttendanceRate(filteredRecords);

    return {
      totalDays,
      presentDays,
      lateDays,
      absentDays,
      attendanceRate,
      status: getAttendanceStatus(attendanceRate)
    };
  } catch (error) {
    console.error('Error getting student attendance summary:', error);
    return {
      totalDays: 0,
      presentDays: 0,
      lateDays: 0,
      absentDays: 0,
      attendanceRate: 0,
      status: '錯誤'
    };
  }
};

/**
 * Check if a student is late based on time
 * @param {string} timeIn - Time student came in
 * @param {string} expectedTime - Expected arrival time
 * @returns {boolean} - True if student is late
 */
export const isStudentLate = (timeIn, expectedTime = '08:00') => {
  try {
    if (!timeIn) return false;
    
    const timeInMinutes = convertTimeToMinutes(timeIn);
    const expectedMinutes = convertTimeToMinutes(expectedTime);
    
    return timeInMinutes > expectedMinutes;
  } catch (error) {
    console.error('Error checking if student is late:', error);
    return false;
  }
};

/**
 * Convert time string to minutes for comparison
 * @param {string} timeString - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
const convertTimeToMinutes = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours * 60 + minutes;
};
